import { useState } from 'react';
import { ErroApi } from '../lib/api';
import {
  escalarLider,
  oQueLidera,
  responderEscala,
  tirarLider,
  type CultoLideranca,
  type EscaladoLideranca,
  type Lider,
} from '../lib/escalaLideres';
import { CORES_STATUS, NOME_STATUS, type Funcao } from '../lib/escalas';
import { horaSP } from '../lib/datas';

type Pedido = {
  titulo: string;
  texto: string;
  confirmar: string;
  campo?: { rotulo: string; sugestao?: string; obrigatorio?: boolean };
  perigo?: boolean;
};

/**
 * Um culto na escala de lideres. O mesmo cartao serve aos dois lados:
 *  - o admin monta ali mesmo, funcao por funcao, sem sair da lista;
 *  - o lider ve quem esta em cada funcao e confirma a propria presenca.
 * A permissao de verdade e do servidor — aqui so se decide o que mostrar.
 */
export default function CartaoLideranca({
  evento,
  funcoes,
  lideres,
  podeEditar,
  meuMembroId,
  confirmar,
  perguntar,
  aoMudar,
  aoErrar,
  semCabecalho = false,
  semMoldura = false,
  comecarAberto = false,
}: {
  evento: CultoLideranca;
  funcoes: Funcao[];
  lideres: Lider[];
  podeEditar: boolean;
  meuMembroId: number | null;
  confirmar: (p: Pedido) => Promise<boolean>;
  perguntar: (p: Pedido) => Promise<{ ok: boolean; texto: string }>;
  aoMudar: () => Promise<void>;
  aoErrar: (mensagem: string) => void;
  semCabecalho?: boolean;
  /** dentro de uma janela o cartao ja tem moldura; outra por fora so pesa */
  semMoldura?: boolean;
  /** quem abriu o culto pelo calendario ja quer montar: nada de clique a mais */
  comecarAberto?: boolean;
}) {
  const [ocupado, setOcupado] = useState<string | null>(null);
  // culto sem ninguem da lideranca abre fechado: a lista tem muitos eventos
  // (limpeza, ensaio) e a grade de funcoes aberta em todos esconderia o que importa
  const [aberto, setAberto] = useState(comecarAberto || evento.escalados.length > 0);

  const noCulto = new Set(evento.escalados.map((p) => p.membroId));
  const livres = lideres.filter((l) => !noCulto.has(l.id));
  const deEquipe = livres.filter((l) => l.lidera.some((x) => x.tipo === 'equipe'));
  const soDeSub = livres.filter((l) => !l.lidera.some((x) => x.tipo === 'equipe'));

  // funcao apagada depois de a escala estar montada: a pessoa continua la
  const nomesAtivos = new Set(funcoes.map((f) => f.nome));
  const orfaos = evento.escalados.filter((p) => !nomesAtivos.has(p.funcao));

  async function executar(chave: string, acao: () => Promise<unknown>, falha: string) {
    setOcupado(chave);
    try {
      await acao();
      await aoMudar();
    } catch (e) {
      aoErrar(e instanceof ErroApi ? e.message : falha);
    } finally {
      setOcupado(null);
    }
  }

  async function escalar(funcao: Funcao, membroId: number) {
    setOcupado(`escalar-${funcao.id}`);
    try {
      await escalarLider({ eventoId: evento.id, membroId, funcaoId: funcao.id });
      await aoMudar();
    } catch (e) {
      // O servidor avisa da indisponibilidade sem decidir pelo admin (409 com
      // saida). Virar isso em erro seco deixaria o lider impossivel de escalar.
      if (e instanceof ErroApi && e.codigo === 'membro_indisponivel') {
        const ok = await confirmar({
          titulo: 'Esse líder avisou que não pode',
          texto: `${e.message} Escalar assim mesmo?`,
          confirmar: 'Escalar mesmo assim',
        });
        if (ok) {
          await executar(
            `escalar-${funcao.id}`,
            () => escalarLider({ eventoId: evento.id, membroId, funcaoId: funcao.id, confirmarMesmoAssim: true }),
            'Não consegui escalar.',
          );
        }
        return;
      }
      aoErrar(e instanceof ErroApi ? e.message : 'Não consegui escalar.');
    } finally {
      setOcupado(null);
    }
  }

  async function tirar(p: EscaladoLideranca) {
    const ok = await confirmar({
      titulo: `Tirar ${p.nome} deste culto?`,
      texto: 'A pessoa sai da escala de líderes deste culto. Dá para escalar de novo depois.',
      confirmar: 'Tirar da escala',
      perigo: true,
    });
    if (!ok) return;
    await executar(`tirar-${p.id}`, () => tirarLider(p.id), 'Não consegui tirar da escala.');
  }

  async function naoVouPoder(p: EscaladoLideranca) {
    const r = await perguntar({
      titulo: 'Não vai poder?',
      texto: 'O administrador fica sabendo para colocar outra pessoa.',
      confirmar: 'Avisar',
      campo: { rotulo: 'Motivo (opcional)' },
    });
    if (!r.ok) return;
    await executar(
      `responder-${p.id}`,
      () => responderEscala(p.id, 'indisponivel', r.texto.trim() || undefined),
      'Não consegui avisar.',
    );
  }

  // funcao que devolve JSX, e nao componente: um componente declarado aqui
  // dentro nasceria de novo a cada render e remontaria a linha inteira
  function linha(p: EscaladoLideranca) {
    const minha = meuMembroId !== null && p.membroId === meuMembroId;
    return (
      <li
        key={p.id}
        className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${
          minha ? 'bg-white ring-2 ring-tinta-900' : 'bg-tinta-100'
        }`}
      >
        <span className="min-w-0 truncate font-medium text-tinta-800">
          {p.nome}
          {minha && <span className="ml-1 text-xs font-semibold text-tinta-400">(você)</span>}
        </span>
        {p.justificativa && p.status === 'indisponivel' && (
          <span className="min-w-0 truncate text-xs text-tinta-400">· {p.justificativa}</span>
        )}
        <span
          className={`ml-auto shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold ${CORES_STATUS[p.status] ?? ''}`}
        >
          {NOME_STATUS[p.status] ?? p.status}
        </span>
        {minha && (
          <span className="flex w-full gap-2 pb-1 pt-1 sm:w-auto sm:pb-0 sm:pt-0">
            {p.status !== 'confirmado' && (
              <button
                disabled={ocupado !== null}
                onClick={() =>
                  void executar(`responder-${p.id}`, () => responderEscala(p.id, 'confirmado'), 'Não consegui confirmar.')
                }
                className="rounded-lg bg-tinta-900 px-3 py-1 text-xs font-semibold text-white hover:bg-tinta-700 disabled:opacity-50"
              >
                Confirmar presença
              </button>
            )}
            {p.status !== 'indisponivel' && (
              <button
                disabled={ocupado !== null}
                onClick={() => void naoVouPoder(p)}
                className="rounded-lg border border-tinta-200 bg-white px-3 py-1 text-xs font-semibold text-tinta-700 hover:bg-tinta-100 disabled:opacity-50"
              >
                Não vou poder
              </button>
            )}
          </span>
        )}
        {podeEditar && (
          <button
            disabled={ocupado !== null}
            onClick={() => void tirar(p)}
            className="shrink-0 text-xs text-tinta-400 hover:text-red-600 disabled:opacity-50"
          >
            tirar
          </button>
        )}
      </li>
    );
  }

  const opcao = (l: Lider) => (
    <option key={l.id} value={l.id} disabled={!l.podeEscalar}>
      {`${l.nome} — ${oQueLidera(l)}${
        l.escalasPelaFrente > 0 ? ` · ${l.escalasPelaFrente}× na escala` : ''
      }${!l.podeEscalar ? (l.temConta ? ' (conta desativada)' : ' (sem conta)') : ''}`}
    </option>
  );

  const vazio = evento.escalados.length === 0;

  return (
    <div className={semMoldura ? '' : 'rounded-2xl bg-white p-4 shadow-cartao'}>
      {!semCabecalho && (
        <>
          <h3 className="text-base font-bold uppercase tracking-wide text-tinta-900">{evento.titulo}</h3>
          <p className="mt-0.5 text-sm text-tinta-400">
            {evento.inicio ? horaSP(evento.inicio) : ''}
            {evento.local ? ` · ${evento.local}` : ''}
          </p>
        </>
      )}

      {!podeEditar && vazio ? (
        <p className="mt-3 text-sm text-tinta-400">Ninguém da liderança escalado ainda.</p>
      ) : podeEditar && vazio && !aberto ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-amber-700">ninguém da liderança escalado</p>
          <button
            onClick={() => setAberto(true)}
            className="rounded-lg bg-tinta-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-tinta-700"
          >
            Montar escala
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {funcoes.map((f) => {
            const naFuncao = evento.escalados.filter((p) => p.funcao === f.nome);
            // quem so olha nao precisa ver funcao vazia uma por uma
            if (!podeEditar && naFuncao.length === 0) return null;
            return (
              <div key={f.id} className="border-b border-tinta-200 pb-3 last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-tinta-800">{f.nome}</span>
                  {podeEditar && livres.length > 0 && (
                    <select
                      value=""
                      disabled={ocupado !== null}
                      aria-label={`Escalar líder em ${f.nome}`}
                      onChange={(ev) => {
                        const membroId = Number(ev.target.value);
                        ev.target.value = '';
                        if (membroId) void escalar(f, membroId);
                      }}
                      className="max-w-[55%] rounded-lg border border-tinta-200 bg-white px-2 py-1 text-sm text-tinta-600"
                    >
                      <option value="">+ escalar líder</option>
                      {deEquipe.length > 0 && <optgroup label="Líderes de equipe">{deEquipe.map(opcao)}</optgroup>}
                      {soDeSub.length > 0 && <optgroup label="Líderes de sub">{soDeSub.map(opcao)}</optgroup>}
                    </select>
                  )}
                </div>
                {naFuncao.length === 0 ? (
                  <p className="mt-1 text-xs text-amber-700">ninguém nesta função</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {naFuncao.map(linha)}
                  </ul>
                )}
              </div>
            );
          })}

          {orfaos.length > 0 && (
            <div>
              <span className="text-sm font-semibold text-tinta-800">Outras funções</span>
              <ul className="mt-2 space-y-1">
                {orfaos.map((p) => (
                  <li key={p.id} className="text-xs text-tinta-400">
                    {p.funcao}:
                    <ul className="mt-1">{linha(p)}</ul>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center gap-4 text-xs">
        <span
          className={`font-semibold ${
            evento.total > 0 && evento.confirmados < evento.total
              ? 'text-amber-600'
              : evento.total > 0
                ? 'text-tinta-900'
                : 'text-tinta-400'
          }`}
        >
          👍 {evento.confirmados}/{evento.total} confirmados
        </span>
      </div>
    </div>
  );
}
